import React from 'react';
import type { Message } from './ChatWidget';
import { ThumbsUp, ThumbsDown, Star, FileText } from 'lucide-react';
import { MarkdownContent } from './MarkdownContent';
import { ChatContentBlocks } from './ChatContentBlocks';
import { toPngDataUrl } from './chatFileLinkPng';
import '../../ChatbotStyles/ChatMessage.scss';
import boticon from '../../images/chatbot/bot-icon.svg?url';
import usericon from '../../images/chatbot/usericon.png';

interface ChatMessageProps {
  message: Message;
  showCitations?: boolean;
  onFeedback: (
    feedbackType: 'liked' | 'disliked' | 'favorited',
    value: boolean,
  ) => void;
  onViewReport?: (reportContent: string, fileLinks?: string[]) => void;
}

export const ChatMessage = ({
  message,
  showCitations = false,
  onFeedback,
  onViewReport,
}: ChatMessageProps) => {
  const isBot = message.role === 'assistant';
  const feedback = message.feedback || {
    liked: false,
    disliked: false,
    favorited: false,
  };

  const isReport = isBot && message.isReport === true;
  const shouldShowReportLink = isReport;
  const hasStructuredContent =
    isBot &&
    message.contentBlocks !== undefined &&
    message.contentBlocks.length > 0;

  const handleLike = () => {
    onFeedback('liked', !feedback.liked);
  };

  const handleDislike = () => {
    onFeedback('disliked', !feedback.disliked);
  };

  const handleFavorite = () => {
    onFeedback('favorited', !feedback.favorited);
  };

  return (
    <div className={`chat-message ${isBot ? 'bot-message' : 'user-message'}`}>
      {isBot && (
        <div className="avatar bot-avatar">
          <img src={boticon} alt="bot-icon" width={25} height={21} />
        </div>
      )}

      <div className={`message-content-wrapper ${isBot ? '' : 'user-content'}`}>
        <div
          className={`message-bubble ${isBot ? 'bot-bubble' : 'user-bubble'}`}
        >
          {shouldShowReportLink ? (
            <div className="report-placeholder">
              <FileText size={18} className="report-icon" />
              <span>Your report is ready. </span>
              <button
                onClick={() =>
                  onViewReport?.(message.content, message.fileLinks)
                }
                className="view-report-link"
              >
                Click here to view the report
              </button>
            </div>
          ) : hasStructuredContent ? (
            <ChatContentBlocks
              contentBlocks={message.contentBlocks!}
              sources={message.sources}
              showCitations={showCitations}
            />
          ) : (
            <MarkdownContent content={message.content} />
          )}

          {!shouldShowReportLink &&
            !hasStructuredContent &&
            message.fileLinks &&
            message.fileLinks.length > 0 && (
              <div className="file-links">
                {message.fileLinks.map((link, index) => {
                  const imgSrc = toPngDataUrl(link);
                  if (!imgSrc) {
                    return null;
                  }
                  return (
                    <div key={index} className="image-container">
                      <img
                        src={imgSrc}
                        alt={`Response image ${index + 1}`}
                        className="response-image"
                        loading="lazy"
                      />
                    </div>
                  );
                })}
              </div>
            )}

          <div className="message-footer">
            <span className="timestamp">
              {message.timeString
                ? message.timeString.match(/\d{2}:\d{2}/)?.[0] || ''
                : message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                    timeZone: 'America/Chicago',
                  })}
            </span>
            {isBot && (
              <div className="feedback-buttons">
                <button
                  onClick={handleLike}
                  className={feedback.liked ? 'liked' : ''}
                  aria-label="Like message"
                >
                  <ThumbsUp
                    fill={feedback.liked ? 'currentColor' : 'none'}
                    stroke={feedback.liked ? 'currentcolor' : 'black'}
                  />
                </button>
                <button
                  onClick={handleDislike}
                  className={feedback.disliked ? 'disliked' : ''}
                  aria-label="Dislike message"
                >
                  <ThumbsDown
                    fill={feedback.disliked ? 'currentColor' : 'none'}
                    stroke={feedback.disliked ? 'currentcolor' : 'black'}
                  />
                </button>
                <button
                  onClick={handleFavorite}
                  className={feedback.favorited ? 'favorited' : ''}
                  aria-label="Favorite message"
                >
                  <Star
                    fill={feedback.favorited ? 'currentColor' : 'none'}
                    stroke={feedback.favorited ? 'currentcolor' : 'black'}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isBot && (
        <div className="avatar user-avatar">
          <img src={usericon} alt="usericon" width={27} height={27} />
        </div>
      )}
    </div>
  );
};
